/**
 * The note tab and its expanded card.
 *
 * The tab is a real trailing column at the table's right edge, tinted to read
 * as chrome rather than data, so row borders and hover states run the full
 * width of the table.
 *
 * Clicking it opens a card as a full-width row directly beneath. Styled as
 * marginalia — indent, left rule, muted ground — so it reads as a comment on
 * the row rather than another row of data. A note is a paragraph, and a
 * paragraph gets a readable line length below the row and a cramped one in a
 * 300px right margin.
 */

import { useEffect, useState } from "react";
import { StickyNote } from "lucide-react";
import { Button } from "@/shared/components/ui/button";
import { Textarea } from "@/shared/components/ui/textarea";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/shared/components/ui/tooltip";
import { cn } from "@/shared/lib/utils";

interface NoteTabProps {
	hasNote: boolean;
	isOpen: boolean;
	onToggle: () => void;
}

export function NoteTab({ hasNote, isOpen, onToggle }: NoteTabProps) {
	return (
		<Tooltip>
			<TooltipTrigger asChild>
				<button
					type="button"
					onClick={onToggle}
					aria-label={hasNote ? "View note" : "Add note"}
					aria-expanded={isOpen}
					className={cn(
						"flex size-full items-center justify-center py-3 transition-colors",
						isOpen
							? "text-foreground"
							: hasNote
								? "text-foreground/70 hover:text-foreground"
								: "text-muted-foreground/35 hover:text-foreground",
					)}
				>
					{/* Filled when a note exists, outline when not — the column doubles
					    as a coverage view without needing a second affordance. */}
					<StickyNote
						className={cn("size-4", hasNote && "fill-current")}
						strokeWidth={hasNote ? 1.75 : 2}
					/>
				</button>
			</TooltipTrigger>
			<TooltipContent side="left">
				{hasNote ? "View note" : "Add note"}
			</TooltipContent>
		</Tooltip>
	);
}

interface NoteCardProps {
	name: string;
	savedNote: string;
	/** Current rating, for the card's context line. Null when unrated. */
	score: string | null;
	saving: boolean;
	onSave: (note: string) => void;
	onClose: () => void;
}

export function NoteCard({
	name,
	savedNote,
	score,
	saving,
	onSave,
	onClose,
}: NoteCardProps) {
	const [draft, setDraft] = useState(savedNote);

	// Re-sync when a refetch lands, so the card never shows stale text.
	useEffect(() => {
		setDraft(savedNote);
	}, [savedNote]);

	const dirty = draft.trim() !== savedNote.trim();

	return (
		<div className="border-l-2 border-l-border bg-muted/40 py-3 pr-4 pl-10">
			<div className="mb-1.5 flex items-baseline gap-2">
				<span className="text-xs font-medium">{name}</span>
				<span className="text-[11px] text-muted-foreground">
					{score != null
						? `rated "${score}" · editing a note never creates a new entry`
						: "no rating yet · a note can be the first thing recorded"}
				</span>
			</div>

			<Textarea
				value={draft}
				onChange={(e) => setDraft(e.target.value)}
				// Matches the backend validator, so the limit shows here rather than
				// arriving as a 400.
				maxLength={4000}
				rows={3}
				autoFocus
				placeholder="What's driving this score?"
				className="bg-background text-sm"
			/>

			<div className="mt-2 flex items-center justify-between gap-3">
				<span />
				<div className="flex items-center gap-2">
					<Button
						type="button"
						variant="ghost"
						size="sm"
						onClick={onClose}
						className="h-7 text-xs"
					>
						Close
					</Button>
					<Button
						type="button"
						size="sm"
						disabled={!dirty || saving}
						onClick={() => onSave(draft)}
						className="h-7 text-xs"
					>
						{saving ? "Saving…" : "Save note"}
					</Button>
				</div>
			</div>
		</div>
	);
}
